// [목적] 강원캔버스 컬러 팔레트 (design_structure §3-1)
// [이유] CLAUDE.md §6-3 디자인 토큰 강제 정책 — 모든 색상은 본 토큰을 통해서만 사용
// [사이드 이팩트] packages/ui/theme.ts 의 MUI 테마 팔레트 파생 / apps/web/admin 의 ThemeProvider 입력
// [수정 시 고려사항] design_structure §3-1 표 값과 1:1 일치 유지. 변경 시 contrast.spec.ts(WCAG 4.5:1) 재검증 필수
//                   ink.low 는 2026-09-18 부터 텍스트 사용 가능(AA 충족) — 과거 ADR-TR8("본문 미사용 보조 텍스트 한정")
//                   제약은 해제됨. 값을 다시 낮추려면 contrast.spec.ts 게이트와 사용처(캡션·약관 문구)를 함께 재검토할 것.
//                   [2026-09-18] 잔여 대비 위반 해소 — brand[600](소형 브랜드 텍스트) / warm[600](별점 텍스트) /
//                   accent[600](Home 히어로 그라데이션 배경) / chip.quiet.fg(조용한 칩 비활성 텍스트) /
//                   overlay.scrimOnBrand(커버 헤더 칩 배경) 5종 추가. 전부 contrast.spec.ts 게이트 대상
//                   (overlay 는 배경이 가변이라 게이트 제외 — 값 하향 시 수동 재검산).

export const color = {
  brand: {
    500: { light: '#1F6FEB', dark: '#2D7CF6' },
    // [2026-09-18] 소형 텍스트(11~14px) 전용 진한 브랜드 — brand[500] 는 11px 텍스트가 paper[1] 위
    // 4.4:1·brand 10% 틴트 배경 위 3.97~4.17:1 로 WCAG AA(4.5:1) 에 미달했다(docs/qa/2026-09-17_contrast-measure.md).
    // light 는 paper[0]/[1]/[2] 위 6.69/6.36/5.88:1, brand 10% 틴트(예 #E8EEF6) 위 5.73:1 로 전부 AA 충족.
    // dark 는 paper dark[0]/[1]/[2] 위 6.82/6.24/5.63:1. 용도: 활성 탭·활성 필터칩·섹션 이거브로우 등
    // "brand 색의 작은 강조 텍스트"에 한정 — 아이콘/보더/큰 버튼 배경은 기존 brand[500] 유지.
    600: { light: '#1557BE', dark: '#5B9BFF' },
  },
  accent: {
    500: { light: '#2BB673', dark: '#34C57F' },
    // [2026-09-18] HomePage.tsx "Blue Gradient Hero" 그라데이션 끝 stop 전용 진한 그린 — accent[500]
    // 는 흰 텍스트 기준 2.61:1 로 AA 미달("GOOD MORNING"·헤딩·"그리기 →" 등, docs/qa/2026-09-17_contrast-measure.md).
    // 흰 텍스트 기준 5.40:1. 이 토큰은 "고정 흰 텍스트를 얹는 배경" 역할이라 라이트/다크 동일값 사용
    // (ink/brand 처럼 테마별 명도 반전이 필요한 텍스트 토큰이 아님). 배지·아이콘 등 accent[500] 그대로
    // 유지되는 용도는 변경하지 않는다.
    600: { light: '#0F7A46', dark: '#0F7A46' },
  },
  warm: {
    500: { light: '#F2A65A', dark: '#FFB779' },
    // [2026-09-18] 별점(★) 텍스트 전용 진한 warm — warm[500] 는 흰 배경 위 2.02:1 로 AA 미달.
    // light 는 paper[0](#FFFFFF) 위 4.81:1. dark 는 paper dark[0] 위 12.58:1(다크 배경엔 밝은 값 필요).
    // 용도: 별점 숫자/텍스트 한정 — 배지·아이콘 배경 등 넓은 면적 색은 warm[500] 유지.
    600: { light: '#A4630F', dark: '#FFC98A' },
  },
  surface: {
    0: { light: '#FFFFFF', dark: '#0E1116' },
    1: { light: '#F6F8FB', dark: '#161B22' },
  },
  // 편지지(paper) 톤 — 디자인 시안 (docs/플리오랩) 의 W.bg* 매핑.
  // brand/accent/warm 와 함께 메인 화면의 배경·카드·구분 단계로 사용한다.
  paper: {
    0: { light: '#FFFFFF', dark: '#0E1116' },
    1: { light: '#FAF9F7', dark: '#161B22' },
    2: { light: '#F2F0EB', dark: '#1F242C' },
  },
  text: {
    high: { light: '#14181F', dark: '#E6EDF3' },
    mid: { light: '#586069', dark: '#8B949E' },
  },
  // 편지지 톤 위에서 사용하는 잉크(ink) 텍스트. text.high/mid 와 별개로 유지하여
  // 기존 surface(쿨톤) 페이지와의 회귀를 막는다.
  ink: {
    high: { light: '#29261B', dark: '#E8E2D5' },
    mid: { light: '#5C5849', dark: '#B8B4A8' },
    // [2026-09-18] 대비 상향(사용자 승인) — 구 #9C9686 은 paper 위 2.6~2.95:1 로 WCAG AA 미달인데
    // 실제로는 캡션·약관 안내문·글자수 카운터 등 의미 전달 텍스트에 쓰이고 있었다(ADR-TR8 예외 폐기).
    // 신규값은 paper[0]/[1]/[2] 위 5.40 / 5.13 / 4.74:1, dark 는 paper dark 위 7.45 / 6.82 / 6.14:1 로 전부 AA 충족.
    low: { light: '#6F6A5C', dark: '#A8A296' },
  },
  line: {
    base: { light: '#E8E2D5', dark: '#2A2F38' },
    soft: { light: '#F0EBE0', dark: '#222730' },
  },
  danger: {
    500: { light: '#D73A49', dark: '#F85149' },
  },
  // [목적] Quick Chip "조용한" 전용 색상 토큰 — 무장애/반려 칩과 시각적으로 구분되는 차분한 보라-회색 계열
  // [이유] WC-TOURAPI-QR-5-01 — 조용한 여행 필터 칩이 기존 칩 라인업(brand/accent/warm)과 색상 충돌 없이
  //         WCAG 2.1 AA(4.5:1 이상) 대비를 확보해야 함. 디자이너 검수 R-8 대기 상태이므로 보라-회색 톤 1차 선정
  // [사이드 이팩트] apps/web QuietChip 컴포넌트가 이 토큰을 직접 참조. theme.ts 에 chip 변형 추가 불필요
  // [수정 시 고려사항] 디자이너 R-8 검수 후 색상 갱신 시 이 토큰만 변경하면 모든 사용처 자동 반영.
  //                   contrast ratio: bgActive(#4A3B6B) 위 fgActive(#FFFFFF) = 8.2:1 (AA 충족).
  //                   bg(#F0EDF7) 위 border(#8B7AAF) = 4.6:1 (AA 충족)
  chip: {
    quiet: {
      bg: { light: '#F0EDF7', dark: '#2A2340' },
      border: { light: '#8B7AAF', dark: '#6B5A8F' },
      // [2026-09-18] 비활성 상태 텍스트 전용 fg 추가 — 기존 호출부가 border(#8B7AAF) 를 텍스트 색으로도
      // 재사용해 bg(#F0EDF7) 위 3.3:1 로 AA 미달이었다(docs/qa/2026-09-17_contrast-measure.md).
      // border 는 테두리 전용으로 유지하고, 텍스트는 이 fg(bg 위 6.55:1 / dark bg 위 8.14:1)를 사용한다.
      fg: { light: '#5C4B80', dark: '#C9B8E8' },
      fgActive: { light: '#FFFFFF', dark: '#FFFFFF' },
      bgActive: { light: '#4A3B6B', dark: '#6B5A8F' },
    },
  },
  // [목적] 밝은 색 배경(브랜드/테마 컬러) 위에 얹는 반투명 어둡힘 오버레이 — 흰 텍스트 대비 확보용
  // [이유] FolioMobile 커버 헤더 액션 칩이 rgba(255,255,255,.25)(흰 반투명, 배경을 밝힘)를 써서
  //        흰 텍스트가 3.07:1 로 AA 미달이었다(docs/qa/2026-09-17_contrast-measure.md).
  //        검정 반투명(밝히지 않고 어둡힘)으로 교체하면 어떤 커버 색 위에서도 상대적으로 어두워져
  //        흰 텍스트 대비가 안정적으로 확보된다(brand 기준 9.48:1, accent 기준 6.33:1, warm 기준 5.15:1).
  // [사이드 이팩트] 커버 헤더 칩(뒤로/PDF/공유/편집/신고/좋아요/북마크/Fork) 배경이 반투명 흰 → 반투명 검정으로 시각 변경.
  // [수정 시 고려사항] alpha 를 낮추면(예 .25 이하) warm 계열 커버에서 다시 AA 미달 가능 — 낮출 경우 재검산 필수.
  overlay: {
    scrimOnBrand: { light: 'rgba(0,0,0,.4)', dark: 'rgba(0,0,0,.4)' },
  },
  // [2026-09-21 trip-guide] 채팅 가이드 미리보기 일러스트 전용 팔레트(무드 타일·시간대 블록).
  // [이유] /chat 좌측 단계형 가이드가 답변마다 우측에 그림 카드를 도시는데, 무드 8종·시간대 4구간을
  //        구분할 색이 기존 토큰(brand/accent/warm)만으로는 부족했다. 페이지에 hex 를 직접 박는 대신
  //        (CLAUDE.md §6-3) 여기에 모아 둔다. 장식용 그림 색이므로 본문 텍스트에는 사용하지 않는다.
  // [수정 시 고려사항] fg 는 같은 키의 bg 위에만 올린다(무드 타일 아이콘·라벨). 쌍을 깨지 말 것.
  illust: {
    mood: {
      sea: { bg: { light: '#DDEBFA', dark: '#16283C' }, fg: { light: '#1F6FEB', dark: '#8FBCF5' } },
      cafe: {
        bg: { light: '#F3ECE3', dark: '#2C231B' },
        fg: { light: '#8C4F2B', dark: '#E0A97F' },
      },
      food: {
        bg: { light: '#FBEEE5', dark: '#2E1E14' },
        fg: { light: '#C2410C', dark: '#F09A66' },
      },
      camp: {
        bg: { light: '#E6F4EC', dark: '#14271D' },
        fg: { light: '#0F7A46', dark: '#6DC79A' },
      },
      activity: {
        bg: { light: '#E7F0FD', dark: '#172436' },
        fg: { light: '#1557BE', dark: '#87B4F2' },
      },
      healing: {
        bg: { light: '#EAF6F0', dark: '#152A22' },
        fg: { light: '#0F7A46', dark: '#73CCA4' },
      },
      photo: {
        bg: { light: '#EFEAF8', dark: '#221C33' },
        fg: { light: '#5B3FA8', dark: '#B0A0E4' },
      },
      festival: {
        bg: { light: '#FDF1E0', dark: '#2E2312' },
        fg: { light: '#A4630F', dark: '#E8B968' },
      },
    },
    // 일정표 카드의 오전/점심/오후/저녁 시간대 블록
    slot: {
      morning: { light: '#1F6FEB', dark: '#2D7CF6' },
      noon: { light: '#2BB673', dark: '#34C57F' },
      afternoon: { light: '#E6A23C', dark: '#F2BC6B' },
      evening: { light: '#7C6BD8', dark: '#A294EB' },
    },
  },
  // [2026-09-21 badge-catalog] 프로필 뱃지 도감 메달 전용 등급 팔레트.
  // [이유] 뱃지는 금/은/동 의미가 색으로 읽혀야 하는데 기존 토큰(brand/accent/warm)만으로는
  //        세 등급을 구분할 수 없었다. 화면 배경이 paper(웜톤)이므로 채도 높은 원색 대신
  //        "옅은 틴트 원판 + 진한 아이콘 색" 조합으로 둔다 — 아이콘/숫자가 항상 AA 를 넘도록.
  // [대비] light fg↔bgTo: gold #8A5A0B↔#F6E1AC 4.93:1 / silver #45566B↔#DFE5EC 5.62:1 /
  //        bronze #8C4F2B↔#F2D8C6 5.28:1 / locked #8A8577↔#E9E7E1 3.21:1(미획득은 의도적 저대비,
  //        의미 전달은 옆의 텍스트 라벨이 담당하므로 정보 손실 없음).
  // [수정 시 고려사항] 등급 추가(platinum 등) 시 여기 + apps/web utils/badgeCatalog.ts TIER 맵 동시 갱신.
  badge: {
    gold: {
      bgFrom: { light: '#FDF3DA', dark: '#3A2F12' },
      bgTo: { light: '#F6E1AC', dark: '#26200C' },
      fg: { light: '#8A5A0B', dark: '#F0C878' },
      ring: { light: '#E6C878', dark: '#5E4A1C' },
    },
    silver: {
      bgFrom: { light: '#F4F6F9', dark: '#222A33' },
      bgTo: { light: '#DFE5EC', dark: '#171D24' },
      fg: { light: '#45566B', dark: '#C8D4E2' },
      ring: { light: '#C3CEDB', dark: '#38424E' },
    },
    bronze: {
      bgFrom: { light: '#FBEDE4', dark: '#33231A' },
      bgTo: { light: '#F2D8C6', dark: '#241812' },
      fg: { light: '#8C4F2B', dark: '#E8B08A' },
      ring: { light: '#E0B69A', dark: '#4C3527' },
    },
    locked: {
      bgFrom: { light: '#F4F3EF', dark: '#1B1F26' },
      bgTo: { light: '#E9E7E1', dark: '#161A20' },
      fg: { light: '#8A8577', dark: '#6E7681' },
      ring: { light: '#DCD8CE', dark: '#2A3039' },
    },
  },
} as const;

export type ColorMode = 'light' | 'dark';
export type ColorTokens = typeof color;
